import { useEffect, useMemo, useState } from 'react'
import { Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { useVendors } from '@/hooks/useVendors'
import { useMarketingCalendar, useSaveMarketingCalendar } from '@/hooks/useMarketing'
import type { MarketingEventResponse } from '@/lib/api/session11Types'
import { todayIso } from '@/lib/dates'

type EditRow = {
  id: number | null
  brand: string
  multiplier: number
  start_date: string
  end_date: string
  reason: string
}

// Stable fallback for the calendar query. A fresh `= []` each render made the
// sync effect below fire every render while loading (setRows → render → new
// [] → effect …), an endless render loop that hung the page's tests.
const NO_EVENTS: MarketingEventResponse[] = []

function activeToday(rows: EditRow[]): number {
  const t = todayIso()
  return rows.filter((r) => r.start_date && r.end_date && r.start_date <= t && t <= r.end_date).length
}

function rowFromServer(e: MarketingEventResponse): EditRow {
  return {
    id: e.id,
    brand: e.brand,
    multiplier: e.multiplier,
    start_date: e.start_date,
    end_date: e.end_date,
    reason: e.reason ?? '',
  }
}

function validate(row: EditRow): string | null {
  if (!row.brand.trim()) return 'brand required'
  if (!(row.multiplier >= 0.5 && row.multiplier <= 5.0)) return 'multiplier must be 0.5–5.0'
  if (!row.start_date) return 'start_date required'
  if (!row.end_date) return 'end_date required'
  if (row.start_date > row.end_date) return 'start must be ≤ end'
  return null
}

export default function Marketing() {
  const { data: vendors = [] } = useVendors()
  const { data: events = NO_EVENTS, isLoading } = useMarketingCalendar()
  const save = useSaveMarketingCalendar()
  const [rows, setRows] = useState<EditRow[]>([])

  useEffect(() => {
    setRows(events.map(rowFromServer))
  }, [events])

  const vendorNames = useMemo(() => vendors.map((v) => v.vendor).sort(), [vendors])

  function addRow() {
    setRows((prev) => [
      ...prev,
      { id: null, brand: '', multiplier: 1.0, start_date: todayIso(), end_date: todayIso(), reason: '' },
    ])
  }

  function updateRow(idx: number, patch: Partial<EditRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx))
  }

  async function onSave() {
    const valid: EditRow[] = []
    const invalid: { idx: number; reason: string }[] = []
    rows.forEach((r, idx) => {
      const err = validate(r)
      if (err) invalid.push({ idx, reason: err })
      else valid.push(r)
    })
    if (invalid.length > 0) {
      toast.error(`Skipped ${invalid.length} invalid row(s): ${invalid.map((i) => `#${i.idx + 1} ${i.reason}`).join('; ')}`)
    }
    try {
      await save.mutateAsync(valid.map((r) => ({
        brand: r.brand,
        multiplier: r.multiplier,
        start_date: r.start_date,
        end_date: r.end_date,
        reason: r.reason || null,
      })))
      toast.success(`Saved ${valid.length} promotion(s)`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'save failed'
      toast.error(msg)
    }
  }

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4 max-w-5xl">
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-base font-semibold">Marketing Calendar</h1>
          <span className="ml-2 inline-block px-2 py-0.5 text-xs border border-[var(--line-warm)] bg-[var(--surface-card)] rounded">
            Active today: <strong>{activeToday(rows)}</strong>
          </span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addRow}>
            <Plus className="mr-1 h-3 w-3" />
            Add Row
          </Button>
          <Button size="sm" className="h-7 text-xs" onClick={onSave} disabled={save.isPending}>
            <Save className="mr-1 h-3 w-3" />
            Save Calendar
          </Button>
        </div>
        <p className="text-xs text-[var(--mute)] mb-3">
          Active promotions boost Predicted Demand via the multiplier.
        </p>

        {isLoading ? (
          <div className="space-y-1">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : (
          <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
            <table className="text-xs w-full">
              <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                <tr className="text-left text-[var(--mute)]">
                  <th className="tbl-th">Brand</th>
                  <th className="tbl-th text-right">Multiplier</th>
                  <th className="tbl-th">Start</th>
                  <th className="tbl-th">End</th>
                  <th className="tbl-th">Reason</th>
                  <th className="tbl-th w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-6 text-center text-[var(--mute)]">No promotions. Click “Add Row” to start.</td></tr>
                )}
                {rows.map((r, idx) => (
                  <tr
                    key={`${r.id ?? 'new'}-${idx}`}
                    className={`border-b border-[var(--line-warm)] ${
                      idx % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                    }`}
                  >
                    <td className="px-1 py-0.5">
                      <select
                        value={r.brand}
                        onChange={(e) => updateRow(idx, { brand: e.target.value })}
                        className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent"
                      >
                        <option value="">— select brand —</option>
                        {vendorNames.map((v) => <option key={v} value={v}>{v}</option>)}
                        {/* Preserve a brand value that's no longer in vendor_rules */}
                        {r.brand && !vendorNames.includes(r.brand) && <option value={r.brand}>{r.brand} (unknown)</option>}
                      </select>
                    </td>
                    <td className="px-1 py-0.5 w-24">
                      <input type="number" step="0.01" min="0.5" max="5.0" value={r.multiplier}
                        onChange={(e) => updateRow(idx, { multiplier: Number(e.target.value) || 0 })}
                        className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent text-right tabular-nums" />
                    </td>
                    <td className="px-1 py-0.5 w-36">
                      <input type="date" value={r.start_date}
                        onChange={(e) => updateRow(idx, { start_date: e.target.value })}
                        className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent" />
                    </td>
                    <td className="px-1 py-0.5 w-36">
                      <input type="date" value={r.end_date}
                        onChange={(e) => updateRow(idx, { end_date: e.target.value })}
                        className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent" />
                    </td>
                    <td className="px-1 py-0.5">
                      <input value={r.reason}
                        onChange={(e) => updateRow(idx, { reason: e.target.value })}
                        className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent" />
                    </td>
                    <td className="px-1 py-0.5">
                      <button aria-label="Remove row" onClick={() => removeRow(idx)} className="p-1 hover:text-destructive">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
